import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface UnreadMessagesCount {
  total: number;
  conversations: Record<string, number>;
}

export const useMessages = () => {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      loadUnreadCount();
      subscribeToMessages();
    }
  }, [user]);

  const loadUnreadCount = async () => {
    if (!user) return;

    try {
      // Get conversations where user is a participant
      const { data: participations, error: participationsError } = await supabase
        .from('conversation_participants')
        .select('conversation_id, last_read_at')
        .eq('user_id', user.id)
        .is('left_at', null);

      if (participationsError) throw participationsError;

      if (!participations || participations.length === 0) {
        setUnreadCount(0);
        setLoading(false);
        return;
      }

      let totalUnread = 0;

      // For each conversation, count unread messages
      for (const participation of participations) {
        const { data: messages, error: messagesError } = await supabase
          .from('messages')
          .select('id, created_at, sender_id')
          .eq('conversation_id', participation.conversation_id)
          .neq('sender_id', user.id) // Not sent by current user
          .gte('created_at', participation.last_read_at || '1970-01-01')
          .eq('is_deleted', false);

        if (messagesError) {
          console.error('Error loading messages:', messagesError);
          continue;
        }

        totalUnread += messages?.length || 0;
      }

      setUnreadCount(totalUnread);
    } catch (error) {
      console.error('Error loading unread count:', error);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToMessages = () => {
    if (!user) return;

    try {
      const channel = supabase
        .channel('messages-changes')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'messages'
          },
          (payload) => {
            console.log('New message:', payload);
            // Ottimizzato: incrementa il count invece di ricaricare tutto
            if (payload.new.sender_id !== user.id) {
              setUnreadCount(prev => prev + 1);
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'conversation_participants',
            filter: `user_id=eq.${user.id}`
          },
          (payload) => {
            console.log('Conversation participation updated:', payload);
            loadUnreadCount(); // Reload when read status changes
          }
        )
        .subscribe((status) => {
          if (status === 'CHANNEL_ERROR') {
            console.warn('WebSocket not available, realtime updates disabled');
          }
        });

      return () => {
        try {
          supabase.removeChannel(channel);
        } catch (error) {
          console.warn('Error removing channel:', error);
        }
      };
    } catch (error) {
      console.warn('WebSocket not available, realtime updates disabled:', error);
      return () => {}; // Return empty cleanup function
    }
  };

  return {
    unreadCount,
    loading,
    refetch: loadUnreadCount
  };
};